/* ============================================================
   MAISON LYS US QUIZ — Lymphatic drainage funnel (US market)
   ============================================================
   - Forked from oren-quiz.js — same engine, plus :
     · echo screens ([data-echo-from]) fed by previous answers
     · congestion score computed from answers
     · dynamic "areas affected" body map
     · SVG decongestion curve + estimated days
   - State in localStorage, step in URL hash (#step=N)
   - Final redirect to LP with personalization params
   ============================================================ */

(function () {
  'use strict';

  const STORAGE_KEY = 'mlQuizUsState';
  const TOTAL_STEPS = 18; // 0 (gender) → 17 (results)

  const root = document.querySelector('[data-ml-quiz-us]');
  const LP_HANDLE = (root && root.dataset.lpHandle) || 'your-drainage-plan';
  const REDIRECT_BASE = (root && root.dataset.redirectBase) || '/pages/';

  /* Labels injected into echo screens ("The {X} you're living with…") */
  const ECHO_LABELS = {
    'brain-fog': 'brain fog',
    'fatigue': 'constant fatigue',
    'heavy-body': 'heavy, weighed-down feeling',
    'low-energy': 'low energy'
  };

  /* Symptom → body area mapping (q_changes + q_healthyweight) */
  const AREA_MAP = {
    'puffy-face': 'face', 'double-chin': 'face',
    'puffy-arms': 'arms', 'puffy-armpits': 'arms',
    'belly-bloat': 'belly', 'belly-pooch': 'belly',
    'heavy-legs': 'legs', 'dimpled-thighs': 'legs', 'thigh-dimples': 'legs'
  };

  /* Same raw answers, mapped to one of the 5 actual body-area photos we have
     (face/belly/legs/thighs/armpits) — used to pick the step 6 info image. */
  const RAW_TO_AREA_IMG = {
    'puffy-face': 'face', 'double-chin': 'face',
    'belly-bloat': 'belly', 'belly-pooch': 'belly',
    'heavy-legs': 'legs',
    'dimpled-thighs': 'thighs', 'thigh-dimples': 'thighs',
    'puffy-armpits': 'armpits', 'puffy-arms': 'armpits'
  };

  const DURATION_LABELS = {
    '0-6m': 'Under 6 months of buildup',
    '6m-2y': '6–24 months of buildup',
    '2-5y': '2–5 years of buildup',
    '5y+': '5+ years of buildup',
    'always': 'Years of buildup'
  };

  const MLQuizUS = {
    state: { step: 0, answers: {} },

    init() {
      this.root = root;
      if (!this.root) return;
      this.trackInit();
      this.loadState();
      this.bindEvents();
      this.bindHash();
      this.goToStep(this.state.step);
    },

    /* ---------- ANALYTICS (same endpoint/protocol as FR quiz) ---------- */
    trackInit() {
      this.trackEndpoint = this.root.dataset.trackEndpoint || '';
      this.quizVariant = this.root.dataset.quizVariant || 'ml-us';
      try {
        this.sessionId = localStorage.getItem('orenQuizSessionId');
        if (!this.sessionId) {
          this.sessionId = this.uuidv4();
          localStorage.setItem('orenQuizSessionId', this.sessionId);
        }
      } catch (e) { this.sessionId = this.uuidv4(); }
      const p = new URLSearchParams(window.location.search);
      this.utm = {
        utm_source: p.get('utm_source') || null,
        utm_medium: p.get('utm_medium') || null,
        utm_campaign: p.get('utm_campaign') || null,
      };
      this.referrer = document.referrer || null;
    },

    uuidv4() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
    },

    track(event, payload) {
      if (!this.trackEndpoint || !this.sessionId) return;
      const body = Object.assign({
        session_id: this.sessionId,
        quiz: this.quizVariant,
        event: event,
        referrer: this.referrer,
        utm_source: this.utm.utm_source,
        utm_medium: this.utm.utm_medium,
        utm_campaign: this.utm.utm_campaign,
      }, payload || {});
      // text/plain = simple CORS, no preflight (sendBeacon-compatible)
      try {
        const json = JSON.stringify(body);
        if (navigator.sendBeacon) {
          navigator.sendBeacon(this.trackEndpoint, new Blob([json], { type: 'text/plain' }));
        } else {
          fetch(this.trackEndpoint, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: json, keepalive: true }).catch(() => {});
        }
      } catch (e) { /* fail silent */ }
    },

    /* ---------- STATE ---------- */
    loadState() {
      try {
        // Every fresh load of the quiz link starts at step 0 — no resuming a
        // previous session's progress across visits.
        localStorage.removeItem(STORAGE_KEY);
        const hashStep = this.parseHashStep();
        if (hashStep !== null) this.state.step = hashStep;
      } catch (e) { console.warn('MLQuizUS: state restore failed', e); }
    },

    saveState() {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state)); } catch (e) {}
    },

    parseHashStep() {
      const m = (window.location.hash || '').match(/step=(\d+)/);
      return m ? Math.min(Math.max(parseInt(m[1], 10), 0), TOTAL_STEPS - 1) : null;
    },

    bindHash() {
      window.addEventListener('hashchange', () => {
        const s = this.parseHashStep();
        if (s !== null && s !== this.state.step) this.goToStep(s);
      });
    },

    /* ---------- EVENTS ---------- */
    bindEvents() {
      this.root.addEventListener('click', (e) => {
        const target = e.target.closest('[data-quiz-action]');
        if (!target) return;
        e.preventDefault();
        const action = target.dataset.quizAction;
        if (action === 'next') this.next();
        else if (action === 'prev') this.prev();
        else if (action === 'skip') {
          this.track('skip', { step: this.state.step });
          const sp = new URLSearchParams({ utm_source: 'quiz', utm_medium: 'onsite', utm_campaign: 'quiz-skip' });
          window.location.href = `${REDIRECT_BASE}${LP_HANDLE}?${sp.toString()}`;
        }
        else if (action === 'answer') this.handleAnswer(target);
        else if (action === 'submit-final') this.submitFinal();
        else if (action === 'restart') { this.track('restart'); this.restart(); }
      });
    },

    handleAnswer(el) {
      const qid = el.dataset.qid;
      const value = el.dataset.value;
      const multi = el.dataset.multi === 'true';
      if (!qid || value === undefined) return;

      if (multi) {
        if (!Array.isArray(this.state.answers[qid])) this.state.answers[qid] = [];
        const idx = this.state.answers[qid].indexOf(value);
        if (idx >= 0) {
          this.state.answers[qid].splice(idx, 1);
          el.classList.remove('is-selected');
        } else {
          this.state.answers[qid].push(value);
          el.classList.add('is-selected');
        }
        this.updateMultiSelectContinueBtn(qid);
        this.saveState();
        this.track('answer', { qid: qid, value: this.state.answers[qid], step: this.state.step });
      } else {
        this.state.answers[qid] = value;
        this.saveState();
        this.track('answer', { qid: qid, value: value, step: this.state.step });
        setTimeout(() => this.next(), 200);
      }
    },

    updateMultiSelectContinueBtn(qid) {
      const stepEl = this.root.querySelector(`[data-step][data-question="${qid}"]`);
      if (!stepEl) return;
      const btn = stepEl.querySelector('[data-quiz-action="next"]');
      if (!btn) return;
      const ans = this.state.answers[qid];
      const count = Array.isArray(ans) ? ans.length : 0;
      btn.disabled = count === 0;
      btn.classList.toggle('is-disabled', count === 0);
    },

    /* ---------- NAVIGATION ---------- */
    next() { this.goToStep(Math.min(this.state.step + 1, TOTAL_STEPS - 1)); },
    prev() { this.goToStep(Math.max(this.state.step - 1, 0)); },

    goToStep(n) {
      this.state.step = n;
      this.saveState();
      this.track('step_view', { step: n });

      const newHash = `#step=${n}`;
      if (window.location.hash !== newHash) history.replaceState(null, '', newHash);

      this.root.querySelectorAll('[data-step]').forEach((el) => {
        el.classList.toggle('is-active', parseInt(el.dataset.step, 10) === n);
      });

      this.restoreSelections(n);
      this.fillEchoes(n);
      this.fillInfoImage(n);
      this.updateProgress(n);
      this.updateBackBtn(n);

      if (n === 16) this.runLoading('loading-1', 17);
      if (n === 17) this.renderResults();

      const top = this.root.getBoundingClientRect().top + window.scrollY - 20;
      window.scrollTo({ top, behavior: 'smooth' });
    },

    restoreSelections(stepNum) {
      const stepEl = this.root.querySelector(`[data-step="${stepNum}"]`);
      if (!stepEl) return;
      const qid = stepEl.dataset.question;
      if (!qid) return;
      const ans = this.state.answers[qid];
      stepEl.querySelectorAll('[data-quiz-action="answer"]').forEach((b) => {
        b.classList.remove('is-selected');
        if (!ans) return;
        if (Array.isArray(ans) && ans.includes(b.dataset.value)) b.classList.add('is-selected');
        else if (ans === b.dataset.value) b.classList.add('is-selected');
      });
      this.updateMultiSelectContinueBtn(qid);
    },

    /* Fill [data-echo-from="qid"] on the visible step with the label of the chosen answer */
    fillEchoes(stepNum) {
      const stepEl = this.root.querySelector(`[data-step="${stepNum}"]`);
      if (!stepEl) return;
      stepEl.querySelectorAll('[data-echo-from]').forEach((el) => {
        const qid = el.dataset.echoFrom;
        const val = this.state.answers[qid];
        const label = (typeof val === 'string' && ECHO_LABELS[val]) || el.dataset.echoDefault || 'fatigue and heaviness';
        el.textContent = label;
      });
    },

    /* Step 6 ("fluid, not fat" info screen) shows one of 5 body-area photos —
       whichever matches what the person actually picked in q_changes /
       q_healthyweight, instead of a fixed generic image for everyone. */
    fillInfoImage(stepNum) {
      const stepEl = this.root.querySelector(`[data-step="${stepNum}"]`);
      if (!stepEl) return;
      const imgs = stepEl.querySelectorAll('[data-info-img]');
      if (!imgs.length) return;

      const a = this.state.answers;
      const picked = [].concat(a.q_changes || [], a.q_healthyweight || []);
      const priority = ['legs', 'thighs', 'belly', 'armpits', 'face'];
      let area = null;
      for (const p of priority) {
        if (picked.some((v) => RAW_TO_AREA_IMG[v] === p)) { area = p; break; }
      }
      if (!area) area = picked.map((v) => RAW_TO_AREA_IMG[v]).find(Boolean) || 'belly';

      imgs.forEach((el) => { el.style.display = el.dataset.infoImg === area ? '' : 'none'; });
    },

    updateBackBtn(n) {
      const btn = this.root.querySelector('[data-quiz-back]');
      if (!btn) return;
      // Hidden on: 0 (first), 16 (loading), 17 (results)
      btn.style.display = [0, 16, 17].indexOf(n) !== -1 ? 'none' : '';
    },

    updateProgress(n) {
      const progressEl = this.root.querySelector('[data-quiz-progress]');
      if (!progressEl) return;
      const showFrom = 0, showTo = 15;
      if (n >= showFrom && n <= showTo) {
        progressEl.style.display = '';
        const pct = ((n - showFrom + 1) / (showTo - showFrom + 2)) * 100;
        const bar = progressEl.querySelector('[data-quiz-progress-bar]');
        if (bar) bar.style.width = pct + '%';
      } else {
        progressEl.style.display = 'none';
      }
    },

    /* ---------- LOADING ---------- */
    runLoading(id, nextStepAfter) {
      const stepEl = this.root.querySelector(`[data-loading="${id}"]`);
      if (!stepEl) return;
      const counter = stepEl.querySelector('[data-loading-counter]');
      const circle = stepEl.querySelector('[data-loading-circle]');
      const items = stepEl.querySelectorAll('[data-loading-item]');
      const duration = 3000;
      const start = Date.now();
      const tick = () => {
        const pct = Math.min(100, Math.round(((Date.now() - start) / duration) * 100));
        if (counter) counter.textContent = pct + '%';
        if (circle) circle.style.setProperty('--quiz-loading-pct', pct);
        items.forEach((item, idx) => {
          if (pct >= (idx / items.length) * 100) item.classList.add('is-done');
        });
        if (pct < 100) requestAnimationFrame(tick);
        else setTimeout(() => this.goToStep(nextStepAfter), 350);
      };
      tick();
    },

    /* ---------- PERSONALIZATION MODEL ---------- */
    getBranch() {
      const a = this.state.answers.q_context;
      if (!a || a === 'none') return 'general';
      if (typeof a !== 'string' || !/^[a-z0-9-]{1,40}$/.test(a)) return 'general';
      return a;
    },

    getScore() {
      const a = this.state.answers;
      let score = 38;
      const changes = Array.isArray(a.q_changes) ? a.q_changes.length : 0;
      const hw = Array.isArray(a.q_healthyweight) ? a.q_healthyweight.length : 0;
      score += Math.min(changes * 5, 25);
      score += Math.min(hw * 3, 18);
      score += ({ '0-6m': 2, '6m-2y': 6, '2-5y': 10, '5y+': 14, 'always': 16 })[a.q_duration] || 0;
      score += ({ 'morning': 4, 'evening': 5, 'cycle': 4, 'always': 9 })[a.q_when] || 0;
      return Math.max(42, Math.min(88, Math.round(score)));
    },

    getSeverity(score) {
      if (score >= 72) return { label: 'Severely congested', cls: 'is-severe' };
      if (score >= 58) return { label: 'Highly congested', cls: 'is-high' };
      return { label: 'Moderately congested', cls: 'is-moderate' };
    },

    getAreas() {
      const a = this.state.answers;
      const set = {};
      ['q_changes', 'q_healthyweight'].forEach((qid) => {
        (Array.isArray(a[qid]) ? a[qid] : []).forEach((v) => {
          if (AREA_MAP[v]) set[AREA_MAP[v]] = true;
        });
      });
      const areas = Object.keys(set);
      return areas.length ? areas : ['legs', 'belly']; // most common per our data
    },

    getDays(score) {
      const a = this.state.answers;
      let days = 52;
      days += ({ '0-6m': 0, '6m-2y': 4, '2-5y': 8, '5y+': 12, 'always': 16 })[a.q_duration] || 0;
      days += Math.max(0, Math.round((score - 60) * 0.4));
      return Math.max(52, Math.min(95, days));
    },

    /* ---------- RESULTS SCREEN ---------- */
    renderResults() {
      const score = this.getScore();
      const severity = this.getSeverity(score);
      const areas = this.getAreas();
      const days = this.getDays(score);

      // Score headline + count-up
      const scoreEls = this.root.querySelectorAll('[data-result-score]');
      const startT = Date.now();
      const animT = 1200;
      const count = () => {
        const p = Math.min(1, (Date.now() - startT) / animT);
        const eased = 1 - Math.pow(1 - p, 3);
        scoreEls.forEach((el) => { el.textContent = Math.round(score * eased) + '%'; });
        if (p < 1) requestAnimationFrame(count);
      };
      count();

      // Gauge marker + fill
      const gauge = this.root.querySelector('[data-result-gauge-fill]');
      if (gauge) setTimeout(() => { gauge.style.width = score + '%'; }, 100);

      // Severity pill
      const sevEl = this.root.querySelector('[data-result-severity]');
      if (sevEl) {
        sevEl.textContent = severity.label + ' — a consistent ritual is recommended';
        sevEl.classList.remove('is-moderate', 'is-high', 'is-severe');
        sevEl.classList.add(severity.cls);
      }

      // Areas affected
      this.root.querySelectorAll('[data-result-area]').forEach((el) => {
        el.classList.toggle('is-on', areas.indexOf(el.dataset.resultArea) !== -1);
      });
      const areaList = this.root.querySelector('[data-result-area-list]');
      if (areaList) {
        const pretty = { face: 'Face', arms: 'Arms & hands', belly: 'Belly', legs: 'Legs & ankles' };
        areaList.textContent = areas.map(z => pretty[z] || z).join(' · ');
      }

      // Duration card
      const durEl = this.root.querySelector('[data-result-duration]');
      if (durEl) durEl.textContent = DURATION_LABELS[this.state.answers.q_duration] || 'Ongoing buildup';

      // Days estimate
      this.root.querySelectorAll('[data-result-days]').forEach((el) => { el.textContent = '~' + days; });

      // Chart
      this.renderChart(score, days);
    },

    /* Two curves over 90 days: with ritual (down to ~10%) vs without (slow creep up) */
    renderChart(score, days) {
      const svg = this.root.querySelector('[data-result-chart]');
      if (!svg) return;
      const W = 320, H = 150, padL = 30, padR = 12, padT = 12, padB = 24;
      const plotW = W - padL - padR, plotH = H - padT - padB;
      const x = (day) => padL + (day / 90) * plotW;
      const y = (pct) => padT + (1 - pct / 100) * plotH;

      // With-ritual: exponential-ish decay from score to 10% reached around `days`
      const withPts = [];
      for (let d = 0; d <= 90; d += 3) {
        const k = 2.2 / days;
        const val = 10 + (score - 10) * Math.exp(-k * d);
        withPts.push(x(d).toFixed(1) + ',' + y(val).toFixed(1));
      }
      // Without: slow creep from score to score+7
      const woPts = [];
      for (let d = 0; d <= 90; d += 9) {
        const val = Math.min(96, score + (d / 90) * 7);
        woPts.push(x(d).toFixed(1) + ',' + y(val).toFixed(1));
      }

      const wEl = svg.querySelector('[data-chart-with]');
      const woEl = svg.querySelector('[data-chart-without]');
      if (wEl) wEl.setAttribute('points', withPts.join(' '));
      if (woEl) woEl.setAttribute('points', woPts.join(' '));

      // Marker at `days`
      const k = 2.2 / days;
      const markVal = 10 + (score - 10) * Math.exp(-k * days);
      const dot = svg.querySelector('[data-chart-dot]');
      if (dot) { dot.setAttribute('cx', x(days).toFixed(1)); dot.setAttribute('cy', y(markVal).toFixed(1)); }
      const lbl = svg.querySelector('[data-chart-label]');
      if (lbl) {
        const lx = Math.min(x(days), W - 62);
        lbl.setAttribute('x', lx.toFixed(1));
        lbl.setAttribute('y', Math.max(12, y(markVal) - 10).toFixed(1));
        lbl.textContent = '~' + days + ' days';
      }
    },

    /* ---------- FINAL SUBMIT ---------- */
    submitFinal() {
      const branch = this.getBranch();
      const score = this.getScore();
      const areas = this.getAreas();
      const days = this.getDays(score);
      const age = this.state.answers.q_age || '';
      const dur = this.state.answers.q_duration || '';

      this.track('finished', { branch: branch, age: age, zone: areas.join(','), severity: String(score), step: this.state.step });

      const params = new URLSearchParams({
        utm_source: 'quiz',
        utm_medium: 'onsite',
        utm_campaign: 'quiz-us-diagnostic',
        branch: branch,
        score: String(score),
        areas: areas.join(','),
        dur: dur,
        days: String(days),
        age: age
      });
      window.location.href = `${REDIRECT_BASE}${LP_HANDLE}?${params.toString()}`;
    },

    restart() {
      if (!confirm('Restart the quiz from the beginning?')) return;
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      this.state = { step: 0, answers: {} };
      this.goToStep(0);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => MLQuizUS.init());
  } else {
    MLQuizUS.init();
  }
  window.MLQuizUS = MLQuizUS;
})();
